import { useState, useEffect, useRef } from "react";
import { MessageCircle, X, Send, Bot, Sparkles, CalendarClock } from "lucide-react";
import { API } from "@/lib/api";

function getSessionId() {
  let id = localStorage.getItem("tsv_chat_session");
  if (!id) {
    id = "sess_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("tsv_chat_session", id);
  }
  return id;
}

const GREETING = "Welcome. I'm TechBot — happy to answer questions about any of our seven ventures: consulting, Walmart wholesaling, credit repair, property, trading, training, or marketing. Or, if you'd prefer, you can reserve a 15-minute conversation below.";

export default function ChatbotWidget({ onBookCall }) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [streaming, setStreaming] = useState(false);
  const sessionId = useRef(getSessionId());
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      if (!res.ok || !res.body) throw new Error("Stream failed");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = line.slice(6);
          try {
            const parsed = JSON.parse(data);
            if (parsed.delta) {
              setMessages((m) => {
                const updated = [...m];
                updated[updated.length - 1] = {
                  role: "assistant",
                  content: updated[updated.length - 1].content + parsed.delta,
                };
                return updated;
              });
            } else if (parsed.error) {
              setMessages((m) => {
                const updated = [...m];
                updated[updated.length - 1] = { role: "assistant", content: "Hmm, I hit a glitch. Try again in a moment." };
                return updated;
              });
            }
          } catch (_e) {
            // ignore malformed SSE chunks
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const updated = [...m];
        updated[updated.length - 1] = { role: "assistant", content: "Connection issue. Please retry." };
        return updated;
      });
    } finally {
      setStreaming(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Floating toggle */}
      <button
        onClick={() => setOpen(!open)}
        data-testid="chatbot-toggle"
        className={`fixed bottom-20 right-6 z-40 w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 ${
          open
            ? "bg-white/10 backdrop-blur-xl border border-white/20"
            : "bg-neon-cyan text-[#05050A] shadow-[0_0_30px_rgba(0,255,255,0.6)] animate-pulse-glow"
        }`}
        aria-label="Open chat"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} strokeWidth={2} />}
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="chatbot-panel"
          className="fixed bottom-36 right-6 z-40 w-[calc(100vw-3rem)] sm:w-[400px] max-h-[70vh] glass-strong flex flex-col overflow-hidden animate-fade-up"
          style={{ boxShadow: "0 0 60px rgba(0,255,255,0.15)" }}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10 bg-black/40">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-neon-cyan/10 border border-neon-cyan/40 flex items-center justify-center">
                <Bot size={18} className="text-neon-cyan" />
              </div>
              <div>
                <div className="font-heading font-bold text-sm">TechBot</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-neon-lime flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-neon-lime animate-pulse" />
                  Online
                </div>
              </div>
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/30 flex items-center gap-1">
              <Sparkles size={10} /> AI
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" style={{ minHeight: 280, maxHeight: 380 }}>
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-msg-${m.role}-${i}`}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-neon-orange text-white"
                      : "glass border border-white/10 text-white/90"
                  }`}
                >
                  {m.content || (streaming && i === messages.length - 1 ? <Dots /> : null)}
                </div>
              </div>
            ))}
          </div>

          {/* Input */}
          <div className="p-3 border-t border-white/10 bg-black/40">
            {onBookCall && (
                <button
                onClick={() => onBookCall()}
                data-testid="chatbot-book-call"
                className="w-full mb-2 flex items-center justify-center gap-2 px-3 py-2 border border-neon-lime/40 bg-neon-lime/5 text-neon-lime text-xs font-mono uppercase tracking-widest hover:bg-neon-lime hover:text-[#05050A] transition-all"
              >
                <CalendarClock size={12} /> Reserve a 15-min Conversation
              </button>
            )}
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                placeholder="Ask about any venture…"
                disabled={streaming}
                data-testid="chatbot-input"
                className="tsv-input !py-2.5 text-sm"
              />
              <button
                onClick={send}
                disabled={streaming || !input.trim()}
                data-testid="chatbot-send"
                className="w-11 h-11 flex-shrink-0 bg-neon-cyan text-[#05050A] flex items-center justify-center hover:bg-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Send size={16} />
              </button>
            </div>
            <div className="font-mono text-[9px] uppercase tracking-widest text-white/20 mt-2 text-center">
              Powered by Claude · Session {sessionId.current.slice(-6)}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Dots() {
  return (
    <span className="inline-flex gap-1">
      <span className="w-1.5 h-1.5 bg-neon-cyan rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
      <span className="w-1.5 h-1.5 bg-neon-cyan rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
      <span className="w-1.5 h-1.5 bg-neon-cyan rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
    </span>
  );
}
